<script setup lang="ts">
import AppIcon from "./AppIcon.vue";
import { useUsage } from "@/composables/useUsage";
import { formatRelative } from "@/lib/format";

/**
 * El detalle del consumo: las ventanas del plan, si el servicio las reporta, y
 * lo que Chuy lleva gastado. Vive aparte porque se enseña en dos lugares —el
 * globito de la barra en computadora y la hoja de abajo en el teléfono—.
 */
const { usage, loading, refresh } = useUsage();
</script>

<template>
  <div>
    <div class="mb-2 flex items-center justify-between gap-2">
      <p class="text-[13px] font-semibold md:text-[12.5px]">Consumo del modelo</p>
      <button
        class="toque grid place-items-center rounded-[7px] p-1 text-tertiary transition hover:bg-surface-hover hover:text-content"
        title="Volver a consultar"
        aria-label="Volver a consultar"
        @click="refresh(true)"
      >
        <AppIcon name="refresh" :size="13" :class="loading ? 'animate-spin-slow' : ''" />
      </button>
    </div>

    <p class="mb-3 text-[12px] leading-snug text-tertiary md:text-[11.5px]">{{ usage?.detail }}</p>

    <div v-if="usage?.windows.length" class="mb-3 space-y-2.5">
      <div v-for="ventana in usage.windows" :key="ventana.key">
        <div class="mb-1 flex items-center justify-between text-[12px] md:text-[11.5px]">
          <span class="text-secondary">{{ ventana.label }}</span>
          <span class="font-semibold tabular-nums">{{ Math.round(ventana.utilization ?? 0) }}%</span>
        </div>
        <span class="block h-1.5 w-full overflow-hidden rounded-full bg-neutral-soft">
          <span
            class="block h-full rounded-full"
            :class="(ventana.utilization ?? 0) >= 85 ? 'bg-bad' : (ventana.utilization ?? 0) >= 60 ? 'bg-warn' : 'bg-ok'"
            :style="{ width: `${Math.min(100, Math.max(2, ventana.utilization ?? 0))}%` }"
          />
        </span>
        <p v-if="ventana.resetsAt" class="mt-0.5 text-[10.5px] text-tertiary">
          se repone {{ formatRelative(ventana.resetsAt) }}
        </p>
      </div>
    </div>

    <dl class="space-y-1 border-t border-separator pt-2.5 text-[12.5px] md:text-[11.5px]">
      <div class="flex items-center justify-between gap-3">
        <dt class="shrink-0 text-tertiary">Proveedor</dt>
        <dd class="min-w-0 truncate text-right">{{ usage?.provider ?? "—" }}</dd>
      </div>
      <div class="flex items-center justify-between gap-3">
        <dt class="text-tertiary">Corridas (7 días)</dt>
        <dd class="tabular-nums">{{ usage?.local.runs7d ?? 0 }}</dd>
      </div>
      <div class="flex items-center justify-between gap-3">
        <dt class="text-tertiary">Tokens (7 días)</dt>
        <dd class="tabular-nums">
          {{ ((usage?.local.inputTokens ?? 0) + (usage?.local.outputTokens ?? 0)).toLocaleString("es-MX") }}
        </dd>
      </div>
      <div class="flex items-center justify-between gap-3">
        <dt class="text-tertiary">Costo estimado</dt>
        <!--
          Solo el servicio que lo reporta deja costo en cada corrida. Con los
          demás, «$0.00» se leería como «no has gastado nada»; mejor la raya.
        -->
        <dd class="tabular-nums">{{ (usage?.local.costUsd ?? 0) > 0 ? `$${(usage?.local.costUsd ?? 0).toFixed(2)}` : "—" }}</dd>
      </div>
    </dl>
  </div>
</template>
